import { useQuery } from '@tanstack/react-query'
import { newsApi } from '@/services/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Eye, Lock, Sparkles, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useSubscription } from '@/contexts/subscription-context'

export function DailyViewsCounter() {
  const { subscription } = useSubscription()
  
  // Don't show for premium users
  const isPremium = subscription?.has_access && subscription?.is_completed
  
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dailyStats'],
    queryFn: async () => {
      const response = await newsApi.getDailyStats()
      return response.data
    },
    enabled: !isPremium, // Only fetch for free users
    refetchInterval: 60000, // Refresh every minute
  })

  // Don't render for premium users
  if (isPremium || isLoading || !stats) return null

  const { remaining_views, state_news_limit, state_news_views_today, curated_articles_count } = stats
  const percentage = ((state_news_limit - remaining_views) / state_news_limit) * 100
  const isLimitReached = remaining_views === 0

  return (
    <Card className={`${isLimitReached ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className={`w-5 h-5 ${isLimitReached ? 'text-red-600' : 'text-blue-600'}`} />
            <CardTitle className="text-lg">Free Daily Articles</CardTitle>
          </div>
          {isLimitReached && (
            <Badge variant="destructive" className="gap-1">
              <Lock className="w-3 h-3" />
              Limit Reached
            </Badge>
          )}
        </div>
        <CardDescription>
          {isLimitReached 
            ? 'You\'ve used all your free state news articles today'
            : 'State news articles remaining today'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-gray-700">
              {remaining_views}/{state_news_limit} remaining
            </span>
            <span className="text-gray-500">{Math.round(percentage)}%</span>
          </div>
          <Progress 
            value={percentage} 
            className={`h-2 ${isLimitReached ? 'bg-red-200' : 'bg-blue-200'}`}
          />
        </div>

        {/* Curated Articles Info */}
        {curated_articles_count > 0 && (
          <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-blue-200">
            <Sparkles className="w-4 h-4 text-yellow-500" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                {curated_articles_count} curated article{curated_articles_count !== 1 ? 's' : ''} available
              </p>
              <p className="text-xs text-gray-500">Free to read, no limits!</p>
            </div>
          </div>
        )}

        {/* Upgrade CTA */}
        {isLimitReached && (
          <div className="pt-2">
            <Link to="/pricing">
              <Button className="w-full gap-2" size="sm">
                Upgrade for Unlimited Access
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        )}

        {/* Info Text */}
        <p className="text-xs text-gray-500 text-center">
          Resets daily at midnight UTC
        </p>
      </CardContent>
    </Card>
  )
}
